import React, { useMemo, useState } from "react";
import s from "../../admin_styles/MailImagesManager.module.css";

function formatBytes(size) {
  if (!Number.isFinite(size)) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export default function MailImagesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copiedName, setCopiedName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [items]
  );

  const loadFiles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/admin/mail-images/files", {
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Ошибка загрузки списка файлов");
      }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) {
      throw new Error("Файл не выбран");
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch("/api/admin/mail-images/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Ошибка загрузки файла");
      }

      setSelectedFile(null);
      setSuccess(`Файл ${file.name} успешно загружен`);
      await loadFiles();
    } catch (err) {
      setError(err.message || "Ошибка загрузки файла");
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Выберите файл для загрузки");
      return;
    }
    await uploadFile(selectedFile);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragOver(false);

    if (uploading) {
      return;
    }

    const file = event.dataTransfer?.files?.[0] || null;
    if (!file) {
      setError("Файл не распознан");
      return;
    }
    if (!file.type?.startsWith("image/")) {
      setError("Можно загружать только изображения");
      return;
    }

    setSelectedFile(file);
    await uploadFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Удалить файл ${filename}?`)) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await fetch("/api/admin/mail-images/delete", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Ошибка удаления файла");
      }

      setSuccess("Файл удален");
      setItems((prev) => prev.filter((x) => x.name !== filename));
    } catch (err) {
      setError(err.message || "Ошибка удаления файла");
    }
  };

  const copyToClipboard = async (text, name) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedName(name);
      setTimeout(() => setCopiedName(""), 1400);
    } catch (err) {
      setError("Не удалось скопировать ссылку");
    }
  };

  React.useEffect(() => {
    loadFiles();
  }, []);

  return (
    <div className={s.wrap}>
      <h3 className={s.title}>Фото для email</h3>

      <div className={s.toolbar}>
        <input
          className={s.fileInput}
          type="file"
          accept="image/*"
          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
        />
        <button className={s.primaryBtn} onClick={handleUpload} disabled={uploading}>
          {uploading ? "Загрузка..." : "Загрузить"}
        </button>
        <button className={s.ghostBtn} onClick={loadFiles} disabled={loading || uploading}>
          Обновить список
        </button>
      </div>

      <div
        className={`${s.dropZone} ${isDragOver ? s.dropZoneActive : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={s.dropTitle}>Перетащите изображение сюда</div>
        <div className={s.dropHint}>или выберите файл через поле выше и нажмите Загрузить</div>
      </div>

      {error && <div className={s.error}>{error}</div>}
      {success && <div className={s.success}>{success}</div>}

      {loading ? (
        <div className={s.info}>Загрузка...</div>
      ) : sortedItems.length === 0 ? (
        <div className={s.info}>В папке пока нет изображений.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Превью</th>
                <th>Файл</th>
                <th>Размер</th>
                <th>Создан</th>
                <th>Ссылка</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => (
                <tr key={item.name}>
                  <td>
                    <img className={s.preview} src={item.url} alt={item.name} />
                  </td>
                  <td className={s.filename}>{item.name}</td>
                  <td>{formatBytes(item.size_bytes)}</td>
                  <td>{new Date(item.created_at).toLocaleString("ru-RU")}</td>
                  <td>
                    <a href={item.url} target="_blank" rel="noreferrer" className={s.link}>
                      открыть
                    </a>
                  </td>
                  <td className={s.actions}>
                    <button
                      className={s.actionBtn}
                      onClick={() => copyToClipboard(item.url, item.name)}
                    >
                      {copiedName === item.name ? "Скопировано" : "Копировать ссылку"}
                    </button>
                    <button className={s.deleteBtn} onClick={() => handleDelete(item.name)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
